import { useMemo, useState } from "react";
import { Bell, HelpCircle, Menu, Stethoscope } from "lucide-react";
import type { AnalyteKey, ClientRecord } from "../types";

export interface TopBarProps {
  onOpenMobileNav: () => void;
  records: ClientRecord[];
  onOpenRecord: (rec: ClientRecord) => void;
  onStartTour: () => void;
  registerTourTarget: (key: string) => (el: HTMLElement | null) => void;
}
export function hasHivOrStdData(r: ClientRecord): boolean {
  const hasHiv = Boolean(r.hiv.diagnosisDate || r.hiv.reportedDate);
  const hasStd = (Object.keys(r.stdTests) as AnalyteKey[]).some((k) => r.stdTests[k].length > 0);
  return hasHiv || hasStd;
}
export function TopBar({ onOpenMobileNav, records, onOpenRecord, onStartTour, registerTourTarget }: TopBarProps) {
  const [notifOpen, setNotifOpen] = useState<boolean>(false);

  const newRecords = useMemo(
    () => records.filter((r) => r.recStatus === "New"),
    [records]
  );

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 md:px-6">
      <div className="flex items-center gap-2 md:hidden">
        <button onClick={onOpenMobileNav} title="Open menu" className="text-slate-500 hover:text-slate-800">
          <Menu className="h-5 w-5" />
        </button>
      </div>
      <div>
        <p className="text-xs text-slate-400">FDOH</p>
      </div>
      <div className="flex items-center gap-4">
        <button ref={registerTourTarget("help")} onClick={onStartTour} title="Take a tour" className="text-slate-400 hover:text-slate-600">
          <HelpCircle className="h-5 w-5" />
        </button>
        <div ref={registerTourTarget("bell")} className="relative">
          <button
            onClick={() => setNotifOpen((o) => !o)}
            title="Notifications"
            className="relative text-slate-400 hover:text-slate-600"
          >
            <Bell className="h-5 w-5" />
            {newRecords.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-rose-500" />
            )}
          </button>

          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <p className="text-sm font-semibold text-slate-800">New HIV / STD Records</p>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{newRecords.length}</span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {newRecords.length === 0 ? (
                    <p className="px-4 py-6 text-center text-xs text-slate-400">No newly added HIV or STD records.</p>
                  ) : (
                    newRecords.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => {
                          onOpenRecord(r);
                          setNotifOpen(false);
                        }}
                        className="flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition last:border-0 hover:bg-slate-50"
                      >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-50 text-cyan-600">
                          <Stethoscope className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-slate-800">
                            New record: {r.firstName} {r.lastName}
                            {hasHivOrStdData(r) ? "" : " (pending data)"}
                          </span>
                          <span className="block truncate text-xs text-slate-400">{r.hospital} – {r.facility}</span>
                          <span className="block text-xs text-slate-400">{r.createdDate}</span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2.5 border-l border-slate-200 pl-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">AU</div>
          <div className="hidden leading-tight sm:block">
            <p className="text-sm font-medium text-slate-800">Admin User</p>
            <p className="text-[11px] text-slate-400">Administrator</p>
          </div>
        </div>
        <div className="hidden border-l border-slate-200 pl-4 sm:block">
          <FlDohLogo className="h-8 w-auto" />
        </div>
      </div>
    </header>
  );
}

export function FlDohLogo({ className }: { className?: string }) {
  return (
    <svg className={className} width="116" height="64" viewBox="0 0 116 64" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Florida Department of Health logo">
      <g clipPath="url(#fldoh-clip)">
        <path d="M5.56522 9.28302V15.6455H13.6294V20.6434H5.56522V32.5251H3.8147e-06V4.23907H14.097V9.28302H5.56522Z" fill="#F78E1E" />
        <path d="M16.2664 32.5251V0H21.64V32.5251H16.2664Z" fill="#F78E1E" />
        <path d="M34.5565 33.1383C28.4777 33.1383 24.2769 27.7648 24.2769 21.686C24.2769 15.6071 28.47 10.2336 34.5565 10.2336C40.643 10.2336 44.836 15.6071 44.836 21.686C44.836 27.7648 40.597 33.1383 34.5565 33.1383ZM34.5565 15.1319C31.3983 15.1319 29.7502 18.5277 29.7502 21.686C29.7502 24.8442 31.3983 28.24 34.5565 28.24C37.7147 28.24 39.3628 24.8442 39.3628 21.686C39.3628 18.5277 37.7147 15.1319 34.5565 15.1319Z" fill="#F78E1E" />
        <path d="M53.4598 19.3249V32.5251H48.0862V20.1298C48.0862 17.0176 46.9517 14.8482 44.5064 12.9625L49.0291 10.1799C50.3476 11.2607 51.2445 12.5869 51.7657 13.6217C52.287 13.1005 55.2536 10.1339 58.6034 10.2259L59.6383 15.6915C56.1504 15.6915 53.8891 18.658 53.4675 19.3249H53.4598Z" fill="#F78E1E" />
        <path d="M64.345 7.45095C62.5512 7.45095 61.1407 5.98682 61.1407 4.24673C61.1407 2.50665 62.5589 1.04252 64.345 1.04252C66.131 1.04252 67.5492 2.45299 67.5492 4.24673C67.5492 6.04048 66.0851 7.45095 64.345 7.45095ZM61.662 32.5251V10.8391H67.0356V32.5251H61.662Z" fill="#F78E1E" />
        <path d="M89.8024 33.1843C88.8595 32.5251 87.633 30.969 87.0658 29.8881C86.2149 31.1146 83.9075 33.1383 80.1821 33.1383C73.9116 33.1383 70.2398 27.7648 70.2398 21.6859C70.2398 15.6071 73.9116 10.2336 80.1821 10.2336C82.9187 10.2336 84.8504 11.9813 85.364 12.4489V0H90.7376V23.2421C90.7376 26.3543 91.8721 28.5237 94.3174 30.4094L89.7947 33.192L89.8024 33.1843ZM80.5193 15.1319C77.3611 15.1319 75.713 18.5277 75.713 21.6859C75.713 24.8442 77.3611 28.24 80.5193 28.24C83.6776 28.24 85.3717 24.8902 85.3717 21.6859C85.3717 18.4817 83.6776 15.1319 80.5193 15.1319Z" fill="#F78E1E" />
        <path d="M110.975 33.1843C110.691 32.9927 109.135 31.2526 108.568 30.4017C108.046 31.107 105.647 33.1383 102.251 33.1383C97.6826 33.1383 94.5704 30.3557 94.5704 26.0707C94.5704 21.4023 98.4798 18.8574 104.237 18.8574H106.544C106.544 16.3584 105.509 14.8483 103.294 14.8483C101.5 14.8483 99.852 15.5535 99.1467 15.9828L97.0234 12.1653C97.8282 11.598 100.603 10.2336 103.577 10.2336C109.281 10.2336 111.918 13.8134 111.918 18.6274V23.2497C111.918 26.362 113.044 28.5313 115.497 30.4171L110.975 33.1997V33.1843ZM104.229 22.5828C101.354 22.5828 100.036 24.0929 100.036 25.879C100.036 28.0484 101.592 29.0373 102.957 29.0373C105.456 29.0373 106.912 26.6303 106.912 26.6303C106.82 26.063 106.628 24.8825 106.582 23.9473C106.582 23.6177 106.536 22.8128 106.536 22.5828H104.229Z" fill="#F78E1E" />
        <path d="M14.5186 63.3408V51.5588H5.54222V63.3408H0.0229988V35.2847H5.54222V46.5991H14.5186V35.2847H20.0378V63.3408H14.5186Z" fill="#00A0AF" />
        <path d="M23.0198 63.3408V35.2847H37.937V40.2903H28.539V46.1392H36.5342V51.0988H28.539V58.3505H38.4046V63.3561H23.0198V63.3408Z" fill="#00A0AF" />
        <path d="M57.7219 64L55.8055 58.1052H46.1239L44.2075 64L38.6882 62.6432L48.6995 35.2847H53.1915L63.2488 62.6432L57.7296 64H57.7219ZM50.9378 42.9963L47.6186 53.2835H54.257L50.9378 42.9963Z" fill="#00A0AF" />
        <path d="M63.6704 63.3408V35.2847H69.1896V58.3428H78.8253V63.3484H63.678L63.6704 63.3408Z" fill="#00A0AF" />
        <path d="M87.0428 40.2903V63.3484H81.5235V40.2903H74.5095V35.2847H94.0568V40.2903H87.0428Z" fill="#00A0AF" />
        <path d="M109.396 63.3408V51.5588H100.419V63.3408H94.9V35.2847H100.419V46.5991H109.396V35.2847H114.915V63.3408H109.396Z" fill="#00A0AF" />
      </g>
      <defs>
        <clipPath id="fldoh-clip">
          <rect width="115.497" height="64" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

